import { delay } from '@/utils/DelayHelper';

type Task<T = unknown> = () => Promise<T>;

/**
 * A rate limiter that limits the rate at which tasks are executed
 * @example
 * ```
 * const rateLimiter = new RateLimiter(60); // 60 tasks per minute
 * const task = async () => {
 *  await delay(1000);
 *  console.log(new Date().toISOString());
 * }
 * rateLimiter.run(task);
 * rateLimiter.run(task);
 * ```
 * Will print something like:
 * ```
 * 2021-08-24T23:34:06.000Z
 * 2021-08-24T23:34:07.000Z // 1000 ms delay
 * ```
 */
export class RateLimiter {
  private queue: Task[];

  private lastExcutionTimestamp: number;

  private isRunning: boolean;

  constructor(private rateLimit: number) {
    this.queue = [];
    this.isRunning = false;
    this.lastExcutionTimestamp = 0;
  }

  /**
   * Execute a task in a rate-limited manner and return a promise that resolves when the task is completed.
   * @param task The task to execute.
   * @returns The promise that resolves when the task is completed.
   */
  public run<T>(task: Task<T>): Promise<T> {
    let resolver: (value: T) => void;
    let rejecter: (error: unknown) => void;
    const promise = new Promise<T>((resolve, reject) => {
      resolver = resolve;
      rejecter = reject;
    });
    const rateLimitedTask = async () => {
      try {
        const result = await task();
        resolver(result);
      } catch (error) {
        rejecter(error);
      }
    };
    this.queue.push(rateLimitedTask);
    this.processQueue();

    return promise;
  }

  /**
   * Process the queue of tasks in a rate-limited manner.
   */
  private async processQueue() {
    if (this.queue.length === 0) {
      return;
    }

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    const now = Date.now();
    const elapsedTimeSinceLastExecution = now - this.lastExcutionTimestamp;
    const timeToWait = this.rateLimit - elapsedTimeSinceLastExecution;
    // If the time to wait is negative, it means that the rate limit has been exceeded, so we should wait for the remaining time.
    if (timeToWait > 0) {
      await delay(timeToWait);
    }

    this.lastExcutionTimestamp = Date.now();
    const task = this.queue.shift();
    try {
      await task();
    } finally {
      this.isRunning = false;
      this.processQueue();
    }
  }
}
